import React, { useState, useRef, useEffect } from 'react';

const SCALES = [
  { log: 27, label: 'Observable universe', sublabel: '~8.8×10²⁶ m', color: '#4a9eff', ruachLabel: 'The expanse of creation', ruachSub: '"He stretches out the heavens like a canopy." — Isaiah 40:22', type: 'cosmos' },
  { log: 23, label: 'Milky Way galaxy', sublabel: '~10²³ m (100,000 light years)', color: '#4a9eff', ruachLabel: 'Our galaxy', ruachSub: '"He determines the number of the stars." — Psalm 147:4', type: 'cosmos' },
  { log: 16, label: 'Solar system', sublabel: '~10¹⁶ m', color: '#f0c040', ruachLabel: 'The solar order', ruachSub: '"The sun rises and the sun sets." — Ecclesiastes 1:5', type: 'cosmos' },
  { log: 7, label: 'Earth', sublabel: '~1.3×10⁷ m diameter', color: '#50e0a0', ruachLabel: 'The earth — אֶרֶץ', ruachSub: '"He laid the foundations of the earth." — Psalm 104:5', type: 'planet' },
  { log: 1, label: 'Human body', sublabel: '~1–2 m', color: '#ffb060', ruachLabel: 'The formed one — אָדָם', ruachSub: '"The LORD God formed the man from the dust of the ground." — Genesis 2:7', type: 'human', highlight: true },
  { log: -2, label: 'Cell', sublabel: '~10–100 µm', color: '#50e0a0', ruachLabel: 'The living unit', ruachSub: '"You knit me together in my mother\'s womb." — Psalm 139:13', type: 'life' },
  { log: -9, label: 'DNA helix', sublabel: '~2 nm width', color: '#50e0a0', ruachLabel: 'The instruction', ruachSub: 'The written code of biological life', type: 'life' },
  { log: -10, label: 'Atom', sublabel: '~0.1 nm = 10⁻¹⁰ m', color: '#4a9eff', ruachLabel: 'The atom — smallest named form', ruachSub: 'Electrons orbit the nucleus at this scale', type: 'atom' },
  { log: -14, label: 'Atomic nucleus', sublabel: '~10⁻¹⁴ m', color: '#ff9040', ruachLabel: 'The nucleus — protons bound', ruachSub: '"In him all things hold together." — Colossians 1:17', type: 'nuclear' },
  { log: -15, label: 'Proton', sublabel: '~0.85 fm = 8.5×10⁻¹⁶ m', color: '#ff9040', ruachLabel: 'The proton — three utterances bound', ruachSub: 'uud — the first binding', type: 'nuclear' },
  { log: -18, label: 'Quark (upper limit)', sublabel: '< 10⁻¹⁸ m', color: '#4a9eff', ruachLabel: 'The quark — utterance', ruachSub: 'Point-like to current measurements. The Ruach-given forms.', type: 'quantum' },
  { log: -18, label: 'W & Z bosons (range)', sublabel: '~10⁻¹⁸ m', color: '#ff9040', ruachLabel: 'The boundary of the weak force', ruachSub: 'Where the Ruach-weighted W and Z bosons operate', type: 'quantum' },
  { log: -18, label: 'Electron (upper limit)', sublabel: '< 10⁻²² m', color: '#50e0a0', ruachLabel: 'The living spark — point-like', ruachSub: 'No measured size — point particle to current precision', type: 'quantum' },
  { log: -35, label: 'Planck length', sublabel: '~1.6×10⁻³⁵ m', color: 'rgba(255,255,255,0.4)', ruachLabel: 'Tohu va-Bohu — תֹהוּ וָבֹהוּ', ruachSub: 'Below this, space and time lose meaning. The boundary of our model. "Where were you when I laid the earth\'s foundation?" — Job 38:4', type: 'planck' },
];

const MIN_LOG = -35, MAX_LOG = 27;

export default function ScaleNavigator({ mode }) {
  const [logScale, setLogScale] = useState(1);
  const canvasRef = useRef(null);
  const animRef = useRef(null);
  const isRuach = mode === 'ruach';
  const accentColor = isRuach ? '#ffb060' : '#f0c040';

  const currentScale = SCALES.reduce((closest, s) => {
    return Math.abs(s.log - logScale) < Math.abs(closest.log - logScale) ? s : closest;
  }, SCALES[0]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const W = canvas.width, H = canvas.height;
    let t = 0;

    const draw = () => {
      t += 0.012;
      ctx.clearRect(0, 0, W, H);

      const scale = currentScale;
      const cx = W / 2, cy = H / 2;

      // Background context visualization
      if (scale.type === 'cosmos') {
        // Star field
        for (let i = 0; i < 80; i++) {
          const seed = i * 137.5;
          const sx = (seed % W);
          const sy = ((seed * 0.618) % H);
          const brightness = 0.3 + 0.5 * Math.sin(t * 0.5 + i);
          ctx.beginPath(); ctx.arc(sx, sy, 0.8 + (i % 3) * 0.4, 0, Math.PI * 2);
          ctx.fillStyle = `rgba(255,255,255,${brightness * 0.6})`; ctx.fill();
        }
        // Galaxy spiral hint
        for (let i = 0; i < 200; i++) {
          const angle = i * 0.15 + t * 0.05;
          const r = i * 0.6;
          const sx = cx + Math.cos(angle) * r;
          const sy = cy + Math.sin(angle) * r * 0.4;
          if (sx < 0 || sx > W || sy < 0 || sy > H) continue;
          ctx.beginPath(); ctx.arc(sx, sy, 0.6, 0, Math.PI * 2);
          ctx.fillStyle = `rgba(160,180,255,${0.15 * Math.max(0, 1 - r / 120)})`; ctx.fill();
        }
      } else if (scale.type === 'human') {
        // Human silhouette hint
        ctx.beginPath();
        ctx.arc(cx, cy - 50, 22, 0, Math.PI * 2);
        ctx.fillStyle = 'rgba(255,160,60,0.12)'; ctx.fill();
        ctx.beginPath();
        ctx.roundRect(cx - 18, cy - 28, 36, 65, 8);
        ctx.fillStyle = 'rgba(255,160,60,0.08)'; ctx.fill();
        // Atom orbits inside
        for (let i = 0; i < 3; i++) {
          const angle = t * (1 + i * 0.3) + i * Math.PI * 2 / 3;
          const ox = cx + Math.cos(angle) * (15 + i * 8);
          const oy = cy + Math.sin(angle * 0.6) * (8 + i * 4);
          ctx.beginPath(); ctx.arc(ox, oy, 2, 0, Math.PI * 2);
          ctx.fillStyle = `rgba(80,224,160,${0.4 + 0.3 * Math.sin(t + i)})`; ctx.fill();
        }
      } else if (scale.type === 'atom') {
        // Atom visualization
        ctx.beginPath(); ctx.arc(cx, cy, 8, 0, Math.PI * 2);
        ctx.fillStyle = '#ff904060'; ctx.fill();
        ctx.beginPath(); ctx.arc(cx, cy, 5, 0, Math.PI * 2);
        ctx.fillStyle = '#ff9040'; ctx.fill();
        // Electron orbits
        [35, 55, 75].forEach((r, i) => {
          ctx.beginPath(); ctx.arc(cx, cy, r, 0, Math.PI * 2);
          ctx.strokeStyle = `rgba(80,224,160,0.15)`; ctx.lineWidth = 0.5; ctx.stroke();
          const angle = t * (1.5 - i * 0.3);
          const ex = cx + Math.cos(angle) * r;
          const ey = cy + Math.sin(angle) * r;
          ctx.beginPath(); ctx.arc(ex, ey, 4, 0, Math.PI * 2);
          ctx.fillStyle = '#50e0a0cc'; ctx.fill();
        });
      } else if (scale.type === 'nuclear') {
        // Proton/quark visualization
        ctx.beginPath(); ctx.arc(cx, cy, 35, 0, Math.PI * 2);
        ctx.fillStyle = 'rgba(255,144,64,0.1)'; ctx.fill();
        ctx.strokeStyle = 'rgba(255,144,64,0.3)'; ctx.lineWidth = 1; ctx.stroke();
        // Three quarks
        ['#ff4444', '#44cc44', '#4488ff'].forEach((c, i) => {
          const angle = t * 0.4 + i * Math.PI * 2 / 3;
          const qx = cx + Math.cos(angle) * 16;
          const qy = cy + Math.sin(angle) * 16;
          const grd = ctx.createRadialGradient(qx, qy, 0, qx, qy, 10);
          grd.addColorStop(0, c + 'aa'); grd.addColorStop(1, c + '00');
          ctx.beginPath(); ctx.arc(qx, qy, 10, 0, Math.PI * 2);
          ctx.fillStyle = grd; ctx.fill();
          ctx.beginPath(); ctx.arc(qx, qy, 6, 0, Math.PI * 2);
          ctx.fillStyle = c; ctx.fill();
        });
        // Gluon lines
        for (let i = 0; i < 3; i++) {
          const a1 = t * 0.4 + i * Math.PI * 2 / 3;
          const a2 = t * 0.4 + ((i + 1) % 3) * Math.PI * 2 / 3;
          ctx.beginPath();
          ctx.moveTo(cx + Math.cos(a1) * 16, cy + Math.sin(a1) * 16);
          ctx.lineTo(cx, cy);
          ctx.strokeStyle = `rgba(224,96,255,${0.3 + 0.2 * Math.sin(t * 2 + i)})`;
          ctx.lineWidth = 1.5; ctx.stroke();
        }
      } else if (scale.type === 'quantum') {
        // Point particle with field
        const pulse = 0.6 + 0.4 * Math.sin(t * 2);
        ctx.beginPath(); ctx.arc(cx, cy, 30 * pulse, 0, Math.PI * 2);
        ctx.fillStyle = scale.color.replace(')', `,0.08)`).replace('rgba', 'rgba').replace('rgb(', 'rgba(').replace(')', ',0.08)'); ctx.fill();
        ctx.beginPath(); ctx.arc(cx, cy, 8, 0, Math.PI * 2);
        ctx.fillStyle = scale.color; ctx.globalAlpha = 0.8; ctx.fill();
        ctx.globalAlpha = 1;
        // Uncertainty rings
        for (let i = 1; i <= 3; i++) {
          ctx.beginPath(); ctx.arc(cx, cy, i * 20 * pulse, 0, Math.PI * 2);
          ctx.strokeStyle = scale.color + '30'; ctx.lineWidth = 0.5; ctx.stroke();
        }
      } else if (scale.type === 'planck') {
        // Quantum foam - random fluctuations
        for (let i = 0; i < 40; i++) {
          const px = cx + (Math.sin(i * 2.1 + t * 0.3) * 80);
          const py = cy + (Math.cos(i * 1.7 + t * 0.2) * 60);
          ctx.beginPath(); ctx.arc(px, py, 1 + Math.sin(t * 3 + i) * 1, 0, Math.PI * 2);
          ctx.fillStyle = `rgba(255,255,255,${0.1 + 0.1 * Math.sin(t + i)})`; ctx.fill();
        }
        ctx.font = 'bold 14px Space Mono, monospace';
        ctx.fillStyle = 'rgba(255,255,255,0.3)';
        ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
        ctx.fillText(isRuach ? 'תֹהוּ וָבֹהוּ' : 'quantum foam', cx, cy);
      } else {
        // Cell / DNA
        ctx.beginPath(); ctx.arc(cx, cy, 50, 0, Math.PI * 2);
        ctx.strokeStyle = scale.color + '40'; ctx.lineWidth = 1.5; ctx.stroke();
        ctx.fillStyle = scale.color + '0a'; ctx.fill();
        // DNA helix hint
        for (let i = 0; i < 20; i++) {
          const y = cy - 40 + i * 4;
          const x1 = cx + Math.sin(i * 0.5 + t) * 15;
          const x2 = cx + Math.sin(i * 0.5 + t + Math.PI) * 15;
          ctx.beginPath(); ctx.arc(x1, y, 2, 0, Math.PI * 2);
          ctx.fillStyle = '#50e0a070'; ctx.fill();
          ctx.beginPath(); ctx.arc(x2, y, 2, 0, Math.PI * 2);
          ctx.fillStyle = '#4a9eff70'; ctx.fill();
          if (i % 3 === 0) {
            ctx.beginPath(); ctx.moveTo(x1, y); ctx.lineTo(x2, y);
            ctx.strokeStyle = 'rgba(255,255,255,0.1)'; ctx.lineWidth = 0.5; ctx.stroke();
          }
        }
      }

      animRef.current = requestAnimationFrame(draw);
    };

    animRef.current = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(animRef.current);
  }, [currentScale, isRuach]);

  const pct = ((logScale - MIN_LOG) / (MAX_LOG - MIN_LOG)) * 100;

  return (
    <div>
      <p style={{ color: 'rgba(255,255,255,0.45)', fontSize: '0.75rem', letterSpacing: '0.1em', marginBottom: '1.5rem', fontFamily: 'Space Mono', textTransform: 'uppercase' }}>
        {isRuach ? 'From creation\'s expanse to the Planck void' : 'Drag from the observable universe to the Planck length'}
      </p>

      {/* Scale display */}
      <div style={{ textAlign: 'center', marginBottom: '1rem' }}>
        <div style={{ fontSize: '0.7rem', fontFamily: 'Space Mono', color: 'rgba(255,255,255,0.3)', marginBottom: '0.25rem' }}>
          Current scale: 10^{logScale} meters
        </div>
        <div style={{ fontSize: '1.3rem', fontWeight: 700, fontFamily: 'Syne', color: currentScale.color }}>
          {isRuach ? currentScale.ruachLabel : currentScale.label}
        </div>
        <div style={{ fontSize: '0.78rem', color: 'rgba(255,255,255,0.4)', marginTop: '0.2rem' }}>
          {currentScale.sublabel}
        </div>
      </div>

      {/* Canvas */}
      <canvas ref={canvasRef} width={280} height={200}
        style={{ width: '100%', maxWidth: '280px', display: 'block', margin: '0 auto 1.25rem', borderRadius: '12px', background: 'rgba(0,0,0,0.3)' }} />

      {/* Slider */}
      <div style={{ marginBottom: '0.5rem' }}>
        <input type="range" min={MIN_LOG} max={MAX_LOG} step={1}
          value={logScale} onChange={e => setLogScale(Number(e.target.value))}
          style={{ width: '100%', accentColor }} />
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.62rem', fontFamily: 'Space Mono', color: 'rgba(255,255,255,0.2)', marginTop: '4px' }}>
          <span>Planck length (10^-35)</span>
          <span>Observable universe (10^27)</span>
        </div>
      </div>

      {/* Scripture / description */}
      {isRuach && currentScale.ruachSub && (
        <div style={{ padding: '0.875rem 1rem', borderRadius: '8px', background: `${currentScale.color}0d`, border: `1px solid ${currentScale.color}25`, marginBottom: '1rem' }}>
          <p style={{ fontSize: '0.82rem', color: 'rgba(255,255,255,0.6)', fontStyle: 'italic', lineHeight: 1.7, margin: 0 }}>
            {currentScale.ruachSub}
          </p>
        </div>
      )}

      {/* Scale markers */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
        <div style={{ fontSize: '0.62rem', fontFamily: 'Space Mono', textTransform: 'uppercase', letterSpacing: '0.1em', color: 'rgba(255,255,255,0.2)', marginBottom: '0.4rem' }}>
          {isRuach ? 'Scales of creation' : 'Scale reference points'}
        </div>
        {SCALES.filter((s, i, arr) => arr.findIndex(x => x.log === s.log) === i).map(s => (
          <div key={s.log} onClick={() => setLogScale(s.log)} style={{
            display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '4px 6px',
            borderRadius: '5px', cursor: 'pointer',
            background: currentScale.log === s.log ? `${s.color}15` : 'transparent',
            transition: 'background 0.15s',
          }}>
            <div style={{ width: '10px', height: '10px', borderRadius: '50%', background: s.color, flexShrink: 0, opacity: 0.7 }} />
            <span style={{ fontSize: '0.72rem', fontFamily: 'Space Mono', color: s.color, width: '60px', flexShrink: 0 }}>10^{s.log}</span>
            <span style={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.5)', flex: 1 }}>
              {isRuach ? s.ruachLabel : s.label}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}
